import { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'

const API_URL = import.meta.env.PROD ? '' : 'http://localhost:3001'

const cipherColors: Record<string, { name: string; value: string }> = {
  'Alphanumeric Qabbala': { name: 'text-stone-300', value: 'text-stone-100' },
  'AQ Squares': { name: 'text-[#B89B6B]', value: 'text-[#D4B896]' },
  'AQ Primes': { name: 'text-[#D4B896]', value: 'text-[#EBD4B0]' },
  'AQ Trigonal': { name: 'text-[#EBD4B0]', value: 'text-[#F5E6D0]' },
  'Ordinal': { name: 'text-stone-200', value: 'text-stone-100' },
  'Numeric QWERTY': { name: 'text-green-400', value: 'text-green-200' },
  'NQ Squares': { name: 'text-[#5A9B5A]', value: 'text-[#7ABD7A]' },
  'NQ Primes': { name: 'text-[#6ABD6A]', value: 'text-[#8BDF8B]' },
  'NQ Trigonal': { name: 'text-[#8BDF8B]', value: 'text-[#ABFFAB]' },
  'QWERTY': { name: 'text-lime-400', value: 'text-lime-200' },
  'Standard': { name: 'text-[#E8A830]', value: 'text-[#F8C040]' },
  'Satanic': { name: 'text-red-500', value: 'text-red-300' },
  'Synx': { name: 'text-cyan-400', value: 'text-cyan-200' },
  'Reduction': { name: 'text-[#F0B840]', value: 'text-[#F8D060]' },
  'Vaxen': { name: 'text-violet-400', value: 'text-violet-200' },
  'Unun': { name: 'text-indigo-400', value: 'text-indigo-200' },
  'Based Atlanteanism': { name: 'text-amber-500', value: 'text-amber-300' },
}

// Cipher groups organized by 3 lines
const cipherGroups = [
  // Line 1: Alphanumeric
  ['Alphanumeric Qabbala', 'Ordinal', 'AQ Primes', 'AQ Squares', 'AQ Trigonal'],
  // Line 2: QWERTY
  ['Numeric QWERTY', 'QWERTY', 'NQ Primes', 'NQ Squares', 'NQ Trigonal'],
  // Line 3: Others
  ['Standard', 'Reduction', 'Synx', 'Unun', 'Vaxen', 'Based Atlanteanism', 'Satanic'],
]

// Default highlighted values: triple digits (111-999) and quadruple digits (1111-9999)
const tripleNumbers = [111, 222, 333, 444, 555, 666, 777, 888, 999, 1111, 2222, 3333, 4444, 5555, 6666, 7777, 8888, 9999]

// Get display name with shape symbol
function getCipherDisplayName(name: string): string {
  if (name.includes('Primes')) return `${name} ●`
  if (name.includes('Squares')) return `${name} ■`
  if (name.includes('Trigonal')) return `${name} ▲`
  return name
}

interface User {
  id: string
  username: string
}

interface Shout {
  id: number
  message: string
  username: string
  created_at: string
}

interface Discovery {
  phrase_hash: string
  phrase: string
  ciphers: Record<string, number>
  discoveredBy: string | null
  discoveredAt: string
  like_count: number
  userLiked: boolean
}

interface FeedPageProps {
  user: User | null
}

// Format relative time from a Date object (for cache timestamp)
function formatTimeAgo(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  
  if (diffMs < 0) return 'now'
  if (diffMs < 3000) return 'now'
  
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)
  
  if (diffSec < 60) return `${diffSec}s`
  if (diffMin < 60) return `${diffMin}m`
  if (diffHour < 24) return `${diffHour}h`
  if (diffDay < 7) return `${diffDay}d`
  return `${Math.floor(diffDay / 7)}w`
}

function formatCompactTime(dateString: string): string {
  // SQLite format: "2026-04-03 16:40:38" - ensure it's parsed as UTC
  const date = new Date(dateString + 'Z')  // Append Z to treat as UTC
  return formatTimeAgo(date)
}

export function FeedPage({ user }: FeedPageProps) {
  const [shouts, setShouts] = useState<Shout[]>([])
  const [discoveries, setDiscoveries] = useState<Discovery[]>([])
  const [discoveriesPage, setDiscoveriesPage] = useState(1)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [totalDiscoveries, setTotalDiscoveries] = useState(0)
  const [newMessage, setNewMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isTabVisible, setIsTabVisible] = useState(true)
  const shoutInputRef = useRef<HTMLInputElement>(null)
  const shoutboxRef = useRef<HTMLDivElement>(null)
  
  // Cache state
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  
  // Highlight settings from localStorage (sync with Calculator)
  const [highlightedPhrases] = useState<string[]>(() => {
    try { const s = localStorage.getItem('highlightedPhrases'); return s ? JSON.parse(s) : [] } catch { return [] }
  })
  const [userHighlightedValues, setUserHighlightedValues] = useState<number[]>([])
  const [disabledDefaultValues, setDisabledDefaultValues] = useState<number[]>([])
  
  // Exponential backoff state
  const [pollInterval, setPollInterval] = useState(2000) // Start at 2s
  const emptyPollCount = useRef(0)
  const prevShoutCount = useRef(0)

  // Load highlight settings from localStorage (sync with Calculator)
  useEffect(() => {
    const loadHighlightSettings = () => {
      try {
        const savedUserValues = localStorage.getItem('userHighlightedValues')
        const savedDisabledDefaults = localStorage.getItem('disabledDefaultValues')
        
        if (savedUserValues) {
          setUserHighlightedValues(JSON.parse(savedUserValues))
        }
        if (savedDisabledDefaults) {
          setDisabledDefaultValues(JSON.parse(savedDisabledDefaults))
        }
      } catch (e) {
        console.error('Error loading highlight settings:', e)
      }
    }
    
    loadHighlightSettings()
    
    // Listen for storage changes (if user updates in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'userHighlightedValues' || e.key === 'disabledDefaultValues') {
        loadHighlightSettings()
      }
    }
    
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  // Check if a cipher value should be highlighted
  const shouldHighlightValue = useCallback((value: number): boolean => {
    // Ensure value is a number for comparison
    const numValue = Number(value)
    
    // User-added values are always highlighted (handle both string and number types)
    const userValues = userHighlightedValues.map(v => Number(v))
    if (userValues.includes(numValue)) {
      console.log('Highlight match for user value:', numValue)
      return true
    }
    
    // Default triple numbers are highlighted only if not disabled
    const disabledValues = disabledDefaultValues.map(v => Number(v))
    if (tripleNumbers.includes(numValue) && !disabledValues.includes(numValue)) {
      console.log('Highlight match for triple number:', numValue)
      return true
    }
    
    return false
  }, [userHighlightedValues, disabledDefaultValues])

  // Debug logging for highlight values
  useEffect(() => {
    console.log('FeedPage highlight values:', { userHighlightedValues, disabledDefaultValues })
  }, [userHighlightedValues, disabledDefaultValues])

  // Leaderboard state
  const [leaderboard, setLeaderboard] = useState<{username: string; discovery_count: number; likes_received: number}[]>([])
  const [leaderboardPage, setLeaderboardPage] = useState(1)
  const [leaderboardTotal, setLeaderboardTotal] = useState(0)
  const [leaderboardLoading, setLeaderboardLoading] = useState(false)
  const [leaderboardSort, setLeaderboardSort] = useState<'discoveries' | 'likes'>('discoveries')

  // Cache key for leaderboard refresh button
  const [leaderboardRefresh, setLeaderboardRefresh] = useState(0)

  // Fetch leaderboard
  useEffect(() => {
    const fetchLeaderboard = async () => {
      setLeaderboardLoading(true)
      try {
        const response = await fetch(`${API_URL}/api/leaderboard?page=${leaderboardPage}&limit=50&sort=${leaderboardSort}`)
        if (response.ok) {
          const data = await response.json()
          setLeaderboard(data.users || [])
          setLeaderboardTotal(data.pagination?.total || 0)
        }
      } catch (e) {
        console.error('Error fetching leaderboard:', e)
      } finally {
        setLeaderboardLoading(false)
      }
    }
    fetchLeaderboard()
  }, [leaderboardPage, leaderboardRefresh, leaderboardSort])

  // Periodic leaderboard refresh
  useEffect(() => {
    if (!isTabVisible) return
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/api/leaderboard?page=${leaderboardPage}&limit=50&sort=${leaderboardSort}`)
        if (response.ok) {
          const data = await response.json()
          setLeaderboard(data.users || [])
          setLeaderboardTotal(data.pagination?.total || 0)
        }
      } catch (e) {
        console.error('Error refreshing leaderboard:', e)
      }
    }, 30000)
    return () => clearInterval(interval)
  }, [isTabVisible, leaderboardPage, leaderboardSort])

  // Scroll shoutbox to bottom
  const scrollToBottom = useCallback(() => {
    if (shoutboxRef.current) {
      shoutboxRef.current.scrollTop = shoutboxRef.current.scrollHeight
    }
  }, [])
  
  // Reset polling to fast mode
  const resetPolling = useCallback(() => {
    setPollInterval(2000)
    emptyPollCount.current = 0
  }, [])

  const fetchShouts = useCallback(async (isBackground = false) => {
    if (!isBackground) setIsFetching(true)
    try {
      const response = await fetch(`${API_URL}/api/shouts`)
      if (!response.ok) throw new Error('Failed to fetch shouts')
      const data = await response.json()
      const newShouts = data.shouts || []
      
      // Check if new messages arrived
      if (newShouts.length > prevShoutCount.current) {
        // New messages! Reset to fast polling
        resetPolling()
      } else if (newShouts.length === prevShoutCount.current) {
        // No new messages, increment empty poll count
        emptyPollCount.current += 1
        
        // After 3 empty polls, start exponential backoff
        if (emptyPollCount.current >= 3) {
          setPollInterval(prev => {
            const next = prev * 2
            return next > 30000 ? 30000 : next // Cap at 30s
          })
        }
      }
      
      prevShoutCount.current = newShouts.length
      setShouts(newShouts)
      setLastUpdated(new Date())
    } catch (err) {
      console.error('Error fetching shouts:', err)
    } finally {
      if (!isBackground) setIsFetching(false)
    }
  }, [resetPolling])

  // Fetch discoveries (paginated, all-time)
  const fetchDiscoveries = useCallback(async (isBackground = false) => {
    if (!isBackground) setIsFetching(true)
    try {
      const session = localStorage.getItem('session')
      const response = await fetch(`${API_URL}/api/discoveries/recent?page=1&limit=50`, {
        headers: session ? { 'Authorization': `Bearer ${session}` } : {}
      })
      if (!response.ok) throw new Error('Failed to fetch discoveries')
      const data = await response.json()
      setDiscoveries(data.discoveries || [])
      setDiscoveriesPage(1)
      setTotalDiscoveries(data.total || 0)
      setLastUpdated(new Date())
    } catch (err) {
      console.error('Error fetching discoveries:', err)
    } finally {
      if (!isBackground) setIsFetching(false)
    }
  }, [])

  // Load more discoveries (next page, appended)
  const loadMoreDiscoveries = useCallback(async () => {
    setIsLoadingMore(true)
    try {
      const nextPage = discoveriesPage + 1
      const session = localStorage.getItem('session')
      const response = await fetch(`${API_URL}/api/discoveries/recent?page=${nextPage}&limit=50`, {
        headers: session ? { 'Authorization': `Bearer ${session}` } : {}
      })
      if (!response.ok) throw new Error('Failed to fetch discoveries')
      const data = await response.json()
      setDiscoveries(prev => [...prev, ...(data.discoveries || [])])
      setDiscoveriesPage(nextPage)
      setLastUpdated(new Date())
    } catch (err) {
      console.error('Error loading more discoveries:', err)
    } finally {
      setIsLoadingMore(false)
    }
  }, [discoveriesPage])

  // Toggle like on a discovery
  const toggleLike = useCallback(async (phraseHash: string) => {
    const session = localStorage.getItem('session')
    const csrfToken = localStorage.getItem('csrfToken')
    if (!session || !csrfToken) return

    try {
      const response = await fetch(`${API_URL}/api/discoveries/${phraseHash}/like`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session}`,
          'X-CSRF-Token': csrfToken,
        },
      })
      if (!response.ok) throw new Error('Failed to toggle like')
      const data = await response.json()
      setDiscoveries(prev => prev.map(d =>
        d.phrase_hash === phraseHash
          ? { ...d, like_count: data.like_count, userLiked: data.liked }
          : d
      ))
    } catch (err) {
      console.error('Error toggling like:', err)
    }
  }, [])

  // Initial load - fetch fresh data
  useEffect(() => {
    fetchShouts(true)
    fetchDiscoveries(true)
  }, [])

  // Scroll to bottom when shouts are loaded
  useEffect(() => {
    scrollToBottom()
  }, [shouts, scrollToBottom])

  // Smart polling with exponential backoff
  useEffect(() => {
    if (!isTabVisible) return
    
    const interval = setInterval(() => { 
      fetchShouts() 
    }, pollInterval)
    
    return () => clearInterval(interval)
  }, [isTabVisible, fetchShouts, pollInterval])

  // Reset polling when tab becomes visible
  useEffect(() => {
    if (isTabVisible) {
      resetPolling()
    }
  }, [isTabVisible, resetPolling])

  useEffect(() => {
    const handleVisibilityChange = () => { setIsTabVisible(!document.hidden) }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" style={{ fontFamily: "'Special Elite', cursive" }}>
        <div className="text-center">
          <p className="text-green-400 text-lg mb-4">Sign in to access the Feed</p>
          <Link to="/" className="text-green-400 hover:text-green-300 underline">Go to Calculator</Link>
        </div>
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user || !newMessage.trim()) return
    
    setIsLoading(true)
    setError(null)
    
    try {
      const session = localStorage.getItem('session')
      const csrfToken = localStorage.getItem('csrfToken')
      
      if (!session || !csrfToken) {
        throw new Error('Not authenticated')
      }
      
      const response = await fetch(`${API_URL}/api/shouts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session}`,
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ message: newMessage.trim() }),
      })
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to send message')
      }
      
      setNewMessage('')
      resetPolling() // Reset to fast polling after sending
      fetchShouts()
    } catch (err: any) {
      setError(err.message || 'Failed to send message')
    } finally {
      setIsLoading(false)
    }
  }

  const messageLength = newMessage.length
  const isOverLimit = messageLength > 100

  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: "'Special Elite', cursive" }}>
      <header className="border-b border-green-900 bg-black/50 sticky top-0 z-40 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-lg font-bold text-green-400">Feed</span>
              {lastUpdated && (
                <span className="text-xs text-gray-500">
                  updated {formatTimeAgo(lastUpdated)} ago
                  {isFetching && <span className="ml-2 text-green-400 animate-spin">⟳</span>}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  fetchShouts()
                  fetchDiscoveries()
                  setLeaderboardRefresh(r => r + 1)
                }}
                disabled={isFetching}
                className="text-xs px-2 py-1 bg-green-900/50 border border-green-700 text-green-400 hover:bg-green-800/50 rounded transition-colors disabled:opacity-50"
                title="Refresh data"
              >
                {isFetching ? '...' : '↻'}
              </button>
              <Link to="/" className="text-green-400 hover:text-green-300 transition-colors">Calculator</Link>
              {user && (
                <>
                  <Link to="/profile" className="text-green-400 hover:text-green-300 transition-colors">Vault</Link>
                  <Link to="/settings" className="text-green-400 hover:text-green-300 transition-colors">Settings</Link>
                </>
              )}
              {user ? (
                <button 
                  onClick={() => {
                    localStorage.removeItem('session')
                    localStorage.removeItem('user')
                    window.location.reload()
                  }}
                  className="text-green-400 hover:text-green-300 transition-colors"
                >
                  Logout
                </button>
              ) : (
                <Link to="/" className="text-green-400 hover:text-green-300 transition-colors">Sign In</Link>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 lg:grid lg:grid-cols-3 gap-6">
        <div className="space-y-6 lg:col-span-2">
          {/* Shoutbox Section */}
          <section className="border border-green-900/30 rounded-xl bg-black/30 p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-green-400">Shoutbox</h2>
              {isFetching && shouts.length === 0 && (
                <span className="text-xs text-green-400 animate-pulse">Loading...</span>
              )}
            </div>
            
            <div ref={shoutboxRef} className="h-48 overflow-y-auto space-y-2 mb-4 pr-2 scrollbar-thin scrollbar-thumb-green-800 scrollbar-track-transparent">
              {shouts.length === 0 ? (
                <p className="text-gray-500 text-sm italic">No messages yet. Be the first to shout!</p>
              ) : (
                shouts.map((shout) => (
                  <div key={shout.id} className="flex items-baseline gap-2 text-sm">
                    <Link to={`/u/${shout.username}`} className="text-green-400 font-medium hover:text-green-300 hover:underline">
                      {shout.username}:
                    </Link>
                    <span className="text-green-300">{shout.message}</span>
                    <span className="text-gray-500 text-xs ml-auto">{formatCompactTime(shout.created_at)}</span>
                  </div>
                ))
              )}
            </div>
            
            {user ? (
              <form onSubmit={handleSubmit} className="flex gap-2">
                <div className="flex-1 relative">
                  <input
                    ref={shoutInputRef}
                    type="text"
                    id="shout-input"
                    name="shout-message"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value.slice(0, 100))}
                    placeholder="Type a message..."
                    maxLength={100}
                    className="w-full px-3 py-2 bg-black border border-green-800 rounded-lg text-green-400 placeholder-green-800 focus:outline-none focus:border-green-600 text-sm"
                  />
                  <span className={`absolute right-3 top-2 text-xs ${isOverLimit ? 'text-red-500' : 'text-gray-500'}`}>
                    {messageLength}/100
                  </span>
                </div>
                <button
                  type="submit"
                  disabled={isLoading || !newMessage.trim() || isOverLimit}
                  className="px-4 py-2 bg-green-900/50 border border-green-600 text-green-400 hover:bg-green-800/50 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {isLoading ? '...' : 'Send'}
                </button>
              </form>
            ) : (
              <p className="text-gray-500 text-sm text-center">
                <Link to="/" className="text-green-400 hover:text-green-300">Sign in</Link> to send messages
              </p>
            )}
            
            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
          </section>

          {/* Recent Discoveries Section */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-green-400">Recent Discoveries <span className="text-xs text-green-500 ml-2 font-normal">(Total: {totalDiscoveries.toLocaleString()})</span></h2>
              {isFetching && discoveries.length === 0 && (
                <span className="text-xs text-green-400 animate-pulse">Loading...</span>
              )}
            </div>
            
            {discoveries.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No discoveries yet.</p>
            ) : (
              <>
              <div className="space-y-4">
                {discoveries.map((discovery) => (
                  <div 
                    key={discovery.phrase_hash}
                    className="border border-green-900/30 rounded-xl bg-black/30 p-4 hover:border-green-700/50 transition-colors"
                  >
                    <div className="flex items-baseline justify-between mb-3">
                      <h3 className={`text-xl font-bold ${highlightedPhrases.some(hp => hp.toLowerCase() === discovery.phrase.toLowerCase()) ? 'text-amber-400' : 'text-green-400'}`}>{discovery.phrase}</h3>
                      <div className="text-right flex items-center gap-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => toggleLike(discovery.phrase_hash)}
                            className={`text-sm transition-colors ${discovery.userLiked ? 'text-red-400' : 'text-gray-500 hover:text-red-400'}`}
                          >
                            {discovery.userLiked ? '❤' : '♡'}
                          </button>
                          <span className="text-xs text-gray-400">{discovery.like_count}</span>
                        </div>
                        <div>
                          {discovery.discoveredBy && (
                            <p className="text-xs text-green-500">
                              {'by: '}
                              <Link to={`/u/${discovery.discoveredBy}`} className="text-green-400 hover:text-green-300 hover:underline">
                                {discovery.discoveredBy}
                              </Link>
                            </p>
                          )}
                          <p className="text-xs text-gray-500">{formatCompactTime(discovery.discoveredAt)}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      {cipherGroups.map((group, groupIndex) => (
                        <div key={groupIndex} className="flex flex-wrap gap-2">
                          {group.map((cipherName) => {
                            const value = discovery.ciphers[cipherName]
                            const colors = cipherColors[cipherName]
                            if (value === undefined) return null
                            
                            return (
                              <div 
                                key={cipherName}
                                className={`flex items-center gap-1 px-2 py-1 rounded bg-black/50 border ${shouldHighlightValue(value) ? 'border-amber-500 bg-amber-900/20' : 'border-green-900/20'}`}
                              >
                                <span className={`text-xs ${colors.name}`}>{getCipherDisplayName(cipherName)}</span>
                                <span className={`text-sm font-bold ${shouldHighlightValue(value) ? 'text-amber-400' : colors.value}`}>{value}</span>
                              </div>
                            )
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {discoveries.length < totalDiscoveries && (
                <div className="text-center mt-6">
                  <button
                    onClick={loadMoreDiscoveries}
                    disabled={isLoadingMore}
                    className="px-6 py-2 bg-green-900/50 border border-green-700 text-green-400 hover:bg-green-800/50 rounded-lg transition-colors disabled:opacity-50 text-sm"
                  >
                    {isLoadingMore ? 'Loading...' : `Show More (${(totalDiscoveries - discoveries.length).toLocaleString()} remaining)`}
                  </button>
                </div>
              )}
              </>
            )}
          </section>
        </div>

        {/* Leaderboard Section */}
        <aside className="border border-green-900/30 rounded-xl bg-black/30 p-4 max-h-[calc(100vh-6rem)] overflow-y-auto scrollbar-thin scrollbar-thumb-green-800 scrollbar-track-transparent">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-amber-400 text-center">🏆 Leaderboard</h2>
            <div className="flex items-center gap-2">
              <div className="flex bg-black/50 border border-green-800/50 rounded text-xs">
                <button
                  onClick={() => { setLeaderboardSort('discoveries'); setLeaderboardPage(1) }}
                  className={`px-2 py-1 rounded-l transition-colors ${leaderboardSort === 'discoveries' ? 'bg-amber-900/50 text-amber-400' : 'text-green-500 hover:text-green-300'}`}
                >
                  Finds
                </button>
                <button
                  onClick={() => { setLeaderboardSort('likes'); setLeaderboardPage(1) }}
                  className={`px-2 py-1 rounded-r transition-colors ${leaderboardSort === 'likes' ? 'bg-red-900/50 text-red-400' : 'text-green-500 hover:text-green-300'}`}
                >
                  ❤
                </button>
              </div>
              {leaderboardLoading && <span className="text-xs text-green-400 animate-pulse">Loading...</span>}
            </div>
          </div>
          
          {leaderboard.length === 0 && !leaderboardLoading ? (
            <p className="text-gray-500 text-sm text-center py-4">No users yet.</p>
          ) : (
            <>
              <ol className="space-y-1.5">
                {leaderboard.map((entry, i) => {
                  const rank = (leaderboardPage - 1) * 50 + i + 1
                  return (
                    <li key={entry.username} className="flex items-center justify-between text-sm py-1 border-b border-green-900/20 last:border-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`text-xs font-mono w-5 text-right flex-shrink-0 ${rank <= 3 ? 'text-amber-400' : 'text-gray-500'}`}>
                          {rank <= 3 ? ['🥇','🥈','🥉'][rank-1] : `#${rank}`}
                        </span>
                        <Link to={`/u/${entry.username}`} className="text-green-400 hover:text-green-300 hover:underline truncate">
                          {entry.username}
                        </Link>
                      </div>
                      <span className="text-xs text-gray-400 ml-2 flex-shrink-0 flex items-center gap-2">
                        <span>{entry.discovery_count}</span>
                        <span className="text-red-400">❤ {entry.likes_received}</span>
                      </span>
                    </li>
                  )
                })}
              </ol>
              
              {/* Pagination */}
              {leaderboardTotal > 50 && (
                <div className="flex items-center justify-center gap-1 mt-4 flex-wrap">
                  {Array.from({ length: Math.ceil(leaderboardTotal / 50) }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setLeaderboardPage(page)}
                      className={`px-2 py-1 text-xs rounded transition-colors ${
                        page === leaderboardPage
                          ? 'bg-amber-900/50 text-amber-400 border border-amber-500/50'
                          : 'text-green-400 hover:text-green-300 border border-green-800/30 hover:border-green-600/50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </aside>
      </main>
    </div>
  )
}
